/**
 * @author Tanmay <tanmay.pawar@314ecorp.com>
 * @description app level utils
 */

import _ from 'lodash';
import { RuleObject } from 'antd/lib/form';
import { message } from 'antd';

import dayjs from 'utils/dayjs';
import EventBus, { Events, Messages } from './EventBus';
import { AUTH_CLIENT } from './url';
import { Hl7BatchMode, ProtocolStatus, Framing, Protocol } from 'client/api';
import { keycloakRoles, REGEX_PATTERNS } from './constant';

export const numberSorter = (a: number, b: number): number => {
	if (a && b) {
		return a - b;
	}
	if (a) {
		return -1;
	}
	if (b) {
		return 1;
	}
	return 0;
};

export const stringSorter = (a: string, b: string): number => {
	if (a && b) {
		return a.localeCompare(b);
	}
	if (a) {
		return -1;
	}
	if (b) {
		return 1;
	}
	return 0;
};

export const compareFalsy = (a: boolean | React.Key, b: string): boolean => {
	return _.isEmpty(a) && _.isEmpty(b);
};

export const portNumberValidator = (__: RuleObject, value: string): Promise<void> => {
	if (_.isNaN(_.toNumber(value))) {
		return Promise.reject(new Error('Invalid Port Number'));
	}
	if (_.toNumber(value) < 1025 || _.toNumber(value) > 65535) {
		return Promise.reject(new Error('Port number should be in between 1025 to 65534'));
	}
	return Promise.resolve();
};

export const IpValidator = (__: RuleObject, value: string): Promise<void> => {
	if (!/^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/.test(value)) {
		return Promise.reject(new Error('Invalid IP address'));
	}

	return Promise.resolve();
};

export const hl7BatchModeOptions = _.map(Hl7BatchMode, (value) => ({
	label: value,
	value,
}));

export const destinationFramingOptions = _.map(Framing, (value) => ({
	label: value,
	value,
}));

export const createSelectOptionFromEnum = (optionEnum: Record<string, any>): { label: string; value: string }[] => {
	return _.map(optionEnum, (item) => ({ label: item, value: item }));
};

export const getCustomerAddress = (addressArray: string[]): string => {
	const nonEmptyValues = _.compact(addressArray);
	const finalAddress = _.join(nonEmptyValues ?? null, ', ');
	return finalAddress ?? '';
};

export const selectOptionCreatorFormList = (items: string[]): AppNS.ISelectOption[] =>
	_.map(items, (item) => ({ label: item, value: item }));

export const displayAlertLimitMessage = (unitsUsage: AppNS.IUnitUsage | undefined, percentage: number): boolean => {
	return (
		!_.isEmpty(unitsUsage) &&
		unitsUsage.maxLimit !== -1 &&
		_.round(unitsUsage.currentCount / unitsUsage.maxLimit, 2) >= percentage
	);
};

export const getInterfaceStatus = (protocols?: Protocol[]): boolean => {
	if (_.isEmpty(protocols)) {
		return false;
	}
	return _.isEmpty(
		_.intersection(_.map(protocols, 'status'), [
			ProtocolStatus.DestinationStopped,
			ProtocolStatus.Disconnected,
			ProtocolStatus.Stopped,
		]),
	);
};

export const hasValidKeycloakRole = (requiredRoles: string[], assignedRoles: string[] | undefined): boolean => {
	if (_.isEmpty(assignedRoles)) {
		return false;
	}
	let environmentSpecificRoles: string[];
	if (_.includes(requiredRoles, keycloakRoles.ADMIN)) {
		environmentSpecificRoles = [
			..._.filter(requiredRoles, (item) => item !== keycloakRoles.ADMIN),
			keycloakRoles.ADMIN,
		];
	} else {
		environmentSpecificRoles = _.filter(requiredRoles, (item) => item !== keycloakRoles.ADMIN);
	}

	return !_.isEmpty(_.intersection(environmentSpecificRoles, assignedRoles));
};

export const dateTimeSorter = <T extends Record<string, any>>(
	key: keyof T,
	customFormat?: string,
): ((a: T, b: T) => number) => {
	if (_.isEmpty(customFormat)) {
		return (a: T, b: T): number => {
			const aDate = dayjs(a[key]);
			const bDate = dayjs(b[key]);
			if (aDate.isBefore(bDate)) {
				return -1;
			}
			if (aDate.isAfter(bDate)) {
				return 1;
			}
			return 0;
		};
	}
	return (a: T, b: T): number => {
		const aDate = dayjs(a[key], customFormat);
		const bDate = dayjs(b[key], customFormat);
		if (aDate.isBefore(bDate)) {
			return -1;
		}
		if (aDate.isAfter(bDate)) {
			return 1;
		}
		return 0;
	};
};

export const getShortHash = (hash?: string): string => {
	return hash?.substring(0, 7) ?? '';
};

export const findShaByFileName = (data: any, fileName?: string): any => {
	return _.flatMap(data, (item: Record<string, any>) => {
		if (item.key === fileName) {
			return [item];
		}
		if (!_.isEmpty(item.children)) {
			return findShaByFileName(item.children, fileName);
		}
		return [];
	});
};

export const publisErrorMessage = (error: Error): void => {
	const responseErrorMsg = _.join(_.map(_.get(error, ['response', 'data', 'error']), 'message'), ' & ');
	EventBus.publish(Events.Error, _.isEmpty(responseErrorMsg) ? Messages.Error : responseErrorMsg);
};

export const getCommonNameValidator = ({
	type,
	customMessage,
	isCertificateName = false,
}: {
	type: string;
	customMessage?: string;
	isCertificateName?: boolean;
}): RuleObject => {
	return {
		pattern: isCertificateName ? REGEX_PATTERNS.CERTIFICATE_NAME : REGEX_PATTERNS.COMMON_NAME,
		message:
			customMessage ??
			`The ${type} name must start with a letter or underscore, followed by letters, numbers or hyphen.`,
	};
};

export const handleCopy = async (text?: string): Promise<void> => {
	if (!text) {
		return;
	}
	try {
		await navigator.clipboard.writeText(text);
		await message.success('Copied to clipboard');
	} catch (err) {
		await message.error('Failed to copy');
	}
};

export const handleDownload = (fileName: string, content?: string): void => {
	if (!content) {
		return;
	}
	const element = document.createElement('a');
	const file = new Blob([content], { type: 'text/plain' });
	element.href = URL.createObjectURL(file);
	element.download = fileName;
	document.body.appendChild(element);
	element.click();
	document.body.removeChild(element);
};

export const getClientId = (): string => {
	return sessionStorage.getItem('AUTH_CLIENT') ?? AUTH_CLIENT;
};

export const getClientIdForRole = (keycloak: any): string => {
	return getClientId() === 'formauth' ? AUTH_CLIENT : keycloak.clientId;
};
